<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { DEFAULT_COMMENT_DRAWER_WIDTH } from '@/store/project'

import CommentCard from '@/components/comment/CommentCard.vue'

const props = defineProps({
  draggable: { type: Boolean, default: false }
})
const emit = defineEmits(['toggle'])

const store = useStore()
const route = useRoute()

const drawerConfig = ref({
  width: DEFAULT_COMMENT_DRAWER_WIDTH,
  borderSize: 3,
  hideItemsThreshold: 700
})

const currentProject = computed(() => store.state.project.currentProject)
const currentTab = computed(() => store.state.comment.currentTab)
const commentItems = computed(() => store.state.comment.items)
const showComments = computed(() => store.state.comment.show)
const loading = computed(() => store.state.comment.commentLoading)
const currentUser = computed(() => store.state.users.currentUser)
const currentStabSchematisationStageId = computed(
  () => store.getters['stabSchematisationStage/currentStabSchematisationStageId']
)

const isInStabSchematisationStageTag = computed(() => route.name === 'schematisation-stage')

const stateComments = computed(() => {
  return isInStabSchematisationStageTag.value
    ? commentItems.value.filter(
        (item) => item.stab_schematisation_stage === currentStabSchematisationStageId.value
      )
    : commentItems.value
})

const commentTitle = computed(() =>
  currentTab.value && 'title' in currentTab.value ? currentTab.value.title : ''
)

const fetchComments = (id) => store.dispatch('comment/fetchItems', id)
const fetchOptions = () => store.dispatch('comment/fetchOptions')
const createItem = (item) => store.dispatch('comment/createItem', item)

const addItem = async () => {
  const stabSchematisationStageId = isInStabSchematisationStageTag.value
    ? currentStabSchematisationStageId.value
    : null
  const item = {
    content_type: currentTab.value.model,
    project: currentProject.value.id,
    user: currentUser.value.id,
    stab_schematisation_stage: stabSchematisationStageId
  }
  await createItem(item)
  emit('toggle')
}

watch(
  currentProject,
  async () => {
    await fetchComments()
  },
  { deep: true }
)

onMounted(async () => {
  await fetchOptions()
})
</script>
<template>
  <v-navigation-drawer
    ref="commentdrawer"
    clipped
    app
    location="right"
    :width="drawerConfig.width"
    v-model="showComments"
  >
    <v-card variant="flat" :loading="loading" rounded="0">
      <template v-if="loading" v-slot:loader>
        <v-progress-linear color="primary" height="3" indeterminate></v-progress-linear>
      </template>
      <v-list-item class="pt-3">
        <v-list-item-title class="text-h6">
          <div class="d-inline">Comments</div>

          <v-chip class="ml-1 mb-1" label size="small">{{ commentTitle }}</v-chip>
          <v-btn
            class="float-right"
            variant="flat"
            @click="addItem()"
            size="small"
            color="primary"
            append-icon="mdi-plus"
          >
            ADD COMMENT
          </v-btn>
        </v-list-item-title>
      </v-list-item>
    </v-card>

    <div v-if="stateComments?.length" class="pt-3">
      <CommentCard v-for="comment in stateComments" :key="comment.id" :comment="comment" />
    </div>
    <div v-else class="pa-4">
      <v-alert type="info" dense variant="tonal">
        No comments available, click the button above to add a comment.
      </v-alert>
    </div>
  </v-navigation-drawer>
</template>
